import { createRecipeContext, type HTMLChakraProps, type UnstyledProp } from '@chakra-ui/react';
import type { LucideIcon } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import * as React from 'react';

import { Button } from '../button';
import { Checkbox } from '../checkbox';
import {
  inlineTableHeaderActionLinkColorMap,
  inlineTableHeaderIconColorMap,
  inlineTableHeaderIconMap,
  inlineTableHeaderIconSize,
  type InlineTableHeaderAction,
  type InlineTableHeaderType,
} from './inline-table-header.kds';

export interface InlineTableHeaderProps extends Omit<HTMLChakraProps<'div'>, 'type'>, UnstyledProp {
  /** Visual type — determines background color, border placement, and default icon. */
  type?: InlineTableHeaderType;
  /** Override the default leading icon (or pass `null` to suppress it). */
  icon?: LucideIcon | null;
  /** Action button/link label (non-selected types). Renders a filled button for "actions" type, or a text link with chevron for info/warning/error. */
  actionLabel?: string;
  /** Leading icon for the action button (only used with type="actions"). */
  actionIcon?: LucideIcon;
  /** Show loading spinner on the action button (only used with type="actions"). */
  actionLoading?: boolean;
  /** Disable the action button (only used with type="actions"). */
  actionDisabled?: boolean;
  /** Called when the action button/link is clicked (non-selected types). */
  onAction?: () => void;
  /** Action buttons (selected type only). Each renders as an icon+label button. */
  actions?: InlineTableHeaderAction[];
  /** Checkbox checked state (only for type="selected"). Supports boolean or 'indeterminate'. */
  checked?: boolean | 'indeterminate';
  /** Called when the checkbox is toggled (only for type="selected"). */
  onCheckedChange?: (details: { checked: boolean | string }) => void;
  /** Message content. */
  children?: React.ReactNode;
}

const { withContext } = createRecipeContext({ key: 'inlineTableHeader' });
const Root = withContext('div') as React.ForwardRefExoticComponent<
  HTMLChakraProps<'div'> & UnstyledProp & { type?: string } & React.RefAttributes<HTMLDivElement>
>;

export const InlineTableHeader = React.forwardRef<HTMLDivElement, InlineTableHeaderProps>(
  function InlineTableHeader(props, ref) {
    const {
      type = 'actions',
      icon,
      actionLabel,
      actionIcon: ActionIcon,
      actionLoading,
      actionDisabled,
      onAction,
      actions,
      checked,
      onCheckedChange,
      children,
      ...rest
    } = props;

    const resolvedIcon: LucideIcon | null | undefined = icon === null ? null : (icon ?? inlineTableHeaderIconMap[type]);

    const Icon = resolvedIcon;

    return (
      <Root ref={ref} type={type} {...rest}>
        {type === 'selected' ? (
          <>
            <span style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Checkbox size="sm" aria-label="Select all rows" checked={checked} onCheckedChange={onCheckedChange} />
              {children}
            </span>
            {actions && actions.length > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {actions.map((action, idx) => (
                  <Button
                    key={`${action.label}-${idx}`}
                    variant="ghost"
                    colorPalette={action.variant === 'destructive' ? 'red' : 'brand'}
                    leftIcon={action.icon}
                    onClick={action.onClick}
                  >
                    {action.label}
                  </Button>
                ))}
              </span>
            )}
          </>
        ) : (
          <>
            {Icon && (
              <Icon
                data-testid="inline-table-header-icon"
                size={inlineTableHeaderIconSize}
                style={{ flexShrink: 0, color: inlineTableHeaderIconColorMap[type] }}
              />
            )}
            {children}
            {actionLabel &&
              (type === 'actions' ? (
                <Button
                  leftIcon={ActionIcon}
                  loading={actionLoading}
                  disabled={actionDisabled}
                  onClick={onAction}
                  flexShrink={0}
                >
                  {actionLabel}
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  color={inlineTableHeaderActionLinkColorMap[type]}
                  _hover={{ bg: 'transparent', color: inlineTableHeaderActionLinkColorMap[type] }}
                  rightIcon={ChevronRight}
                  onClick={onAction}
                  flexShrink={0}
                >
                  {actionLabel}
                </Button>
              ))}
          </>
        )}
      </Root>
    );
  }
);
InlineTableHeader.displayName = 'InlineTableHeader';
